<template>
  <Modal ref="modalRef" width="27.5rem" @after-close="reset">
    <template #header>
      <Text variant="h4">{{ step === 'pick' ? 'Profile photo' : 'Crop your photo' }}</Text>
    </template>

    <template #body>
      <div
        v-if="step === 'pick'"
        class="dropzone"
        :class="{ dragging: isDraggingOver }"
        @click="openFileDialog"
        @dragover.prevent="isDraggingOver = true"
        @dragleave="isDraggingOver = false"
        @drop.prevent="onDrop"
      >
        <IconTile icon="upload-cloud-02" color="neutral" />
        <View direction="column" gap="xs" align="center">
          <Text variant="label-medium">
            Drag a photo here or
            <u>browse</u>
          </Text>
          <Text variant="body-small" color="tertiary">
            JPG or PNG · at least 240×240 · up to {{ maxImageUploadMb }} MB
          </Text>
        </View>
        <input ref="fileInputRef" class="file-input" type="file" accept="image/*" @change="onFilePicked" />
      </div>

      <ImageCrop v-else-if="imageUrl" ref="cropRef" :image-url="imageUrl" />
    </template>

    <template #footer>
      <template v-if="step === 'crop'">
        <Button variant="ghost" size="medium" @click="step = 'pick'">Back</Button>
        <Button size="medium" :loading="isSaving" @click="savePhoto">Save photo</Button>
      </template>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { onUnmounted, ref } from 'vue';

import { Button, extractErrorMessage, IconTile, Modal, Text, useAlertsStore, View } from '@tallo/design-system';
import { ImageCrop, checkImageFile, maxImageUploadMb } from '@tallo/file-manager';
import { profileService, useInvestorStore } from '@tallo/investor/investor';

const minDimension = 240;

const investorStore = useInvestorStore();
const alertsStore = useAlertsStore();

const modalRef = ref<InstanceType<typeof Modal>>();
const cropRef = ref<InstanceType<typeof ImageCrop>>();
const fileInputRef = ref<HTMLInputElement>();
const step = ref<'pick' | 'crop'>('pick');
const imageUrl = ref<string | null>(null);
const isDraggingOver = ref(false);
const isSaving = ref(false);

function show() {
  step.value = 'pick';
  modalRef.value?.show();
}

function reset() {
  step.value = 'pick';
  releaseImageUrl();
}

function releaseImageUrl() {
  if (imageUrl.value) {
    URL.revokeObjectURL(imageUrl.value);
    imageUrl.value = null;
  }
}

function openFileDialog() {
  fileInputRef.value?.click();
}

function onFilePicked(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  (event.target as HTMLInputElement).value = '';
  acceptFile(file);
}

function onDrop(event: DragEvent) {
  isDraggingOver.value = false;
  acceptFile(event.dataTransfer?.files?.[0]);
}

async function acceptFile(file?: File) {
  if (!file) return;

  const refusal = checkImageFile(file);

  if (refusal) {
    alertsStore.error(refusal);
    return;
  }

  const url = URL.createObjectURL(file);

  // The crop circle is 180px and we export at 480px, so anything under 240×240 upscales badly
  const dimensions = await readImageDimensions(url).catch(() => null);
  if (!dimensions) {
    URL.revokeObjectURL(url);
    alertsStore.error("That image couldn't be read. Try a different one.");
    return;
  }
  if (dimensions.width < minDimension || dimensions.height < minDimension) {
    URL.revokeObjectURL(url);
    alertsStore.error('The photo must be at least 240×240 pixels');
    return;
  }

  releaseImageUrl();
  imageUrl.value = url;
  step.value = 'crop';
}

function readImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = reject;
    image.src = url;
  });
}

async function savePhoto() {
  isSaving.value = true;

  try {
    const blob = await cropRef.value!.getCroppedBlob();
    const file = new File([blob], 'avatar.png', { type: 'image/png' });
    const user = await profileService.uploadAvatar(file);
    investorStore.setUser(user);
    alertsStore.success('Profile photo updated');
    modalRef.value?.close();
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not save the photo. Please try again.'));
  } finally {
    isSaving.value = false;
  }
}

onUnmounted(releaseImageUrl);

defineExpose({ show });
</script>

<style scoped lang="scss">
.dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--t-spacing-md);
  padding: 2.5rem var(--t-spacing-lg);
  border: 2px dashed var(--t-color-neutral-300);
  border-radius: var(--t-border-radius-md);
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover,
  &.dragging {
    background-color: var(--t-color-neutral-50);
  }
}

.file-input {
  display: none;
}
</style>
